import { useState, useEffect, useContext, useRef } from 'react';
import OtpInput from 'react-otp-input';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavBarContext } from '../App';
import { loginUser, confirmEmailSignUp, resendEmailConfirmation } from '../store/authSlice';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';

import classes from "./SignIn.module.css";
import Modal from "./UI/Modal";
import SocialForm from './UI/SocialForm';
import GermanFlag from "../public/HomepagePhotos/germanFlag.svg";
import closeButton from "../public/icons/Schließen.svg";
import backArrow from "../public/icons/ZurückIcon.svg";
import { getAndClearRedirectPath, getAuthRedirectPath, normalizeRedirectPath } from '../util/redirectUtils';
import { mapCognitoErrorToGerman } from '../util/authErrorMessages';
import { trackLogin } from '../util/tracking';
import { notifySuccessToast } from '../util/notifyActionError';

const OtpChecker = ({ telephone, onOtpSubmit }) => {
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isOtpCorrect, setIsOtpCorrect] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    const correctOtp = '123456';

    if (!otp) {
      setOtpError('Code muss ausgefüllt werden');
    } else if (otp !== correctOtp) {
      setOtpError('Code ist nicht korrekt');
      setIsOtpCorrect(false);
    } else {
      setOtpError('');
      setIsOtpCorrect(true);
      onOtpSubmit();
    }
  };

  return (
    <>
        <div className={classes.telephoneContainer}>
          <div className={classes.telephoneInfosContainer}>
            <span className='regularLarge'>
              Gib den 6-stelligen Code ein, den wir an +49 {telephone} gesendet haben.
            </span>
          </div>
      <div className={classes.otpInput} style={{ marginBottom: otpError ? '15px' : '30px'}}>
        <OtpInput
          value={otp}
          onChange={(otp) => { setOtp(otp); setOtpError(''); }}
          numInputs={6}
          renderInput={(props) => {
            const handleKeyDown = (e) => {
              if (e.key === 'Backspace' && e.currentTarget.value) {
                // When deleting a character, stop propagation to prevent focus change
                e.stopPropagation();
              }
              // Call original onKeyDown if it exists
              if (props.onKeyDown) {
                props.onKeyDown(e);
              }
            };
            return <input {...props} onKeyDown={handleKeyDown} />;
          }}
          shouldAutoFocus
        />
      </div>
          {otpError && 
            <div className={classes.errorContainer}>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
              </svg>
              <span className='slimRegularSmallText'>{otpError}</span>
            </div>
          }
          <button 
            className={classes.buttonWeiter} 
            type="submit" 
            onClick={handleSubmit}
          >
            Weiter
          </button>
          <div className={classes.keinSmsComponent}>
            <span>Keine SMS erhalten? </span>
            <span className={classes.underlineSpan}>Erneut senden</span>
          </div>
        </div>
    </>
  );
};

export const SignIn = ({ open, onClose, onSwitchToRegister }) => {
  const { setShowSignIn } = useContext(NavBarContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector((state) => state.auth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [telephone, setTelephone] = useState('');
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isCodeFocused, setIsCodeFocused] = useState(false);
  const [isNewPasswordFocused, setIsNewPasswordFocused] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [showTelephoneInput, setShowTelephoneInput] = useState(false);
  const [telephoneSubmitted, setTelephoneSubmitted] = useState(false);
  const [otpSubmitted, setOtpSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetSocialForm, setResetSocialForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [forgotPasswordConfirmationCode, setForgotPasswordConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Email confirmation states
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [emailConfirmationCode, setEmailConfirmationCode] = useState('');
  const [isEmailConfirmationCodeFocused, setIsEmailConfirmationCodeFocused] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const emailConfirmationInputRef = useRef(null);

  useEffect(() => {
    // Auto-focus the email confirmation input when it's shown
    if (showEmailConfirmation) {
      const timer = setTimeout(() => {
        if (emailConfirmationInputRef.current) {
          emailConfirmationInputRef.current.focus();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [showEmailConfirmation]);

  useEffect(() => {
    if (auth.status === 'succeeded' && auth.isAuthenticated) {
      const redirectTarget = getAuthRedirectPath(location, normalizeRedirectPath(location));

      setShowSignIn(false);
      onClose();
      navigate(redirectTarget, { replace: true });
    } else if (auth.status === 'failed') {
      setError(mapCognitoErrorToGerman(auth.error));
    } else if (auth.status === 'succeeded' && auth.requiresEmailConfirmation) {
      // Show email confirmation form
      setShowEmailConfirmation(true);
      setEmailSubmitted(false);
      setShowTelephoneInput(false);
      setShowForgotPassword(false);
      setError('');
    }
  }, [auth?.status, auth?.isAuthenticated, auth?.error, auth?.requiresEmailConfirmation, setShowSignIn, onClose, navigate, location]);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e) => {
    if (!e.target.value) {
      setIsFocused(false);
    }
  };

  const handleCodeFocus = () => {
    setIsCodeFocused(true);
  };

  const handleCodeBlur = (e) => {
    if (!e.target.value) {
      setIsCodeFocused(false);
    }
  };

  const handleNewPasswordFocus = () => {
    setIsNewPasswordFocused(true);
  };

  const handleNewPasswordBlur = (e) => {
    if (!e.target.value) {
      setIsNewPasswordFocused(false);
    }
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      setShowSignIn(false);
      onClose();
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (showTelephoneInput) {
      if (!telephone) {
        setError('Telefonnummer muss ausgefüllt werden');
      } else {
        setError('');
        setTelephoneSubmitted(true);
      }
    } else {
      if (!emailSubmitted) {
        if (!email) {
          setError('E-Mail Adresse muss ausgefüllt werden');
        } else if (!validateEmail(email)) {
          setError('E-Mail Adresse ist nicht gültig');
        } else {
          setError('');
          setEmailSubmitted(true);
        }
      } else {
        if (!password) {
          setError('Passwort muss ausgefüllt werden');
        } else {
          try {
            setError('');
            setIsSubmitting(true);
            // Minimal diagnostic for email/password flow
            await dispatch(loginUser({ email, password })).unwrap();
            trackLogin({
              method: 'email',
              source: 'login_modal',
            });
            // Perform immediate redirect on successful unwrap to avoid missing state listeners
            const explicit = sessionStorage.getItem('oauth_redirect_path');
            if (explicit) {
              const target = getAndClearRedirectPath();
              navigate(target || '/', { replace: true });
              return;
            }
          } catch (error) {
            // Localize Cognito / Amplify errors coming from the thunk
            const germanMessage = mapCognitoErrorToGerman(error);
            setError(germanMessage);
          } finally {
            setIsSubmitting(false);
          }
        }
      }
    }
  };

  useEffect(() => {
    if (auth.loginStatus === "rejected" && auth.loginError) {
      setError(auth.loginError);
    } else if (auth.loginStatus === "success") {
      const redirectTarget = getAuthRedirectPath(location, normalizeRedirectPath(location));

      setShowSignIn(false);
      onClose();
      navigate(redirectTarget, { replace: true });
    }
  }, [auth.loginStatus, auth.loginError, onClose, navigate, location, setShowSignIn]);

  const handleBackToTelephone = () => {
    setTelephoneSubmitted(false);
    setResetSocialForm(true);
  };

  const handleTelephoneLogin = () => {
    setError("")
    setShowTelephoneInput(prevState => !prevState);
  };

  const handleOtpSubmit = () => {
    trackLogin({
      method: 'phone',
      source: 'login_modal',
    });
    setOtpSubmitted(true);
    onClose();
    
    navigate(getAuthRedirectPath(location, normalizeRedirectPath(location)), { replace: true });
  };

  // Forgot password handlers
  const handleForgotPasswordClick = () => {
    setError('');
    setShowForgotPassword(true);
    setEmailSubmitted(false);
    setShowTelephoneInput(false);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      setError('E-Mail Adresse muss ausgefüllt werden');
      return;
    }
    
    if (!validateEmail(forgotPasswordEmail)) {
      setError('E-Mail Adresse ist nicht gültig');
      return;
    }

    try {
      setError('');
      setIsSubmitting(true);
      
      // Call AWS Amplify resetPassword (same as in ForgotPasswordScreen.tsx)
      const output = await resetPassword({ username: forgotPasswordEmail });
      
      // Handle the response like in React Native version
      handleResetPasswordNextSteps(output);
      
      // Navigate to next step (like navigation.navigate in React Native)
      setShowResetConfirmation(true);
      
    } catch (error) {
      const germanMessage = mapCognitoErrorToGerman(error);
      setError(germanMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle reset password response (same as in ForgotPasswordScreen.tsx)
  const handleResetPasswordNextSteps = (output) => {
    const { nextStep } = output;
    switch (nextStep.resetPasswordStep) {
      case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
        const codeDeliveryDetails = nextStep.codeDeliveryDetails;
        notifySuccessToast(`Der Bestätigungscode wurde an ${codeDeliveryDetails.destination} gesendet.`, 'Code gesendet');
        // Collect the confirmation code from the user and pass to confirmResetPassword.
        break;
      case 'DONE':
        break;
      default:
        break;
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordConfirmationCode) {
      setError('Bestätigungscode muss ausgefüllt werden');
      return;
    }
    
    if (!newPassword) {
      setError('Neues Passwort muss ausgefüllt werden');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    try {
      setError('');
      setIsSubmitting(true);
      
      // Call AWS Amplify confirmResetPassword (same as in NewPasswordScreen.tsx)
      await confirmResetPassword({
        username: forgotPasswordEmail,
        confirmationCode: forgotPasswordConfirmationCode,
        newPassword
      });
      
      // Reset all states and close modal (like navigation.reset in React Native)
      setShowForgotPassword(false);
      setShowResetConfirmation(false);
      setForgotPasswordEmail('');
      setForgotPasswordConfirmationCode('');
      setNewPassword('');
      setError('');
      
      notifySuccessToast('Passwort erfolgreich zurückgesetzt! Sie können sich jetzt mit Ihrem neuen Passwort anmelden.', 'Passwort zurückgesetzt');
      onClose();
      
    } catch (error) {
      setError(mapCognitoErrorToGerman(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToForgotPassword = () => {
    setShowResetConfirmation(false);
    setForgotPasswordConfirmationCode('');
    setNewPassword('');
    setError('');
  };

  const handleBackToSignIn = () => {
    setShowForgotPassword(false);
    setShowResetConfirmation(false);
    setForgotPasswordEmail('');
    setForgotPasswordConfirmationCode('');
    setNewPassword('');
    setError('');
  };

  // Email confirmation handlers
  const handleEmailConfirmationCodeFocus = () => {
    setIsEmailConfirmationCodeFocused(true);
  };

  const handleEmailConfirmationCodeBlur = (e) => {
    if (!e.target.value) {
      setIsEmailConfirmationCodeFocused(false);
    }
  };

  const handleEmailConfirmationSubmit = async (e) => {
    e.preventDefault();
    
    if (!emailConfirmationCode) {
      setError('Bestätigungscode muss ausgefüllt werden');
      return;
    }

    try {
      setError('');
      setIsSubmitting(true);
      
      await dispatch(confirmEmailSignUp({ 
        email: auth.emailToConfirm || email, 
        confirmationCode: emailConfirmationCode 
      })).unwrap();
      
      // Reset email confirmation state and show success message
      setShowEmailConfirmation(false);
      setEmailConfirmationCode('');
      setEmailSubmitted(false);
      setError('');
      
        notifySuccessToast('E-Mail erfolgreich bestätigt! Sie können sich jetzt mit Ihrem Passwort anmelden.', 'E-Mail bestätigt');
      
    } catch (error) {
      setError(typeof error === 'string' ? error : mapCognitoErrorToGerman(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmailConfirmation = async () => {
    try {
      setResendingCode(true);
      setError('');
      
      await dispatch(resendEmailConfirmation({ 
        email: auth.emailToConfirm || email 
      })).unwrap();
      
      notifySuccessToast('Bestätigungscode wurde erneut gesendet!', 'Code gesendet');
      
    } catch (error) {
      setError(typeof error === 'string' ? error : mapCognitoErrorToGerman(error, 'Fehler beim Senden des Codes'));
    } finally {
      setResendingCode(false);
    }
  };

  const handleBackToEmailConfirmation = () => {
    setShowEmailConfirmation(false);
    setEmailConfirmationCode('');
    setError('');
  };

  return (
    <Modal open={open} onBackdropClick={handleBackdropClick}>
      <div className={classes.outsideContainer}>
      {(telephoneSubmitted || showResetConfirmation || showEmailConfirmation) && (
        <div className={classes.backEmailContainer} onClick={
          showResetConfirmation ? handleBackToForgotPassword : 
          showEmailConfirmation ? handleBackToEmailConfirmation :
          handleBackToTelephone
        }>
          <img src={backArrow} alt='back to email' />
          <span>Zurück</span>
        </div>
      )}
      {showForgotPassword && !showResetConfirmation && (
        <div className={classes.backEmailContainer} onClick={handleBackToSignIn}>
          <img src={backArrow} alt='back to sign in' />
          <span>Zurück</span>
        </div>
      )}
      <div className={classes.modalContainer}>
        <button className={classes.closeButton} onClick={onClose}>
          <img src={closeButton} alt="close-button" />
        </button>
        <h1 className='titleSemibold'>
          {telephoneSubmitted ? 'Nummer bestätigen' : 
           showEmailConfirmation ? 'E-Mail bestätigen' :
           showForgotPassword ? (showResetConfirmation ? 'Passwort zurücksetzen' : 'Passwort vergessen') : 
           'Anmelden'}
        </h1>
        {!telephoneSubmitted && !showForgotPassword && !showEmailConfirmation && (
          <form className={classes.form} onSubmit={handleSubmit} noValidate>
            {showTelephoneInput ? (
              <div className={classes.phoneInputContainer} style={{ marginBottom: error ? '14.5px' : '29.88px'}}>
                <div className={classes.countryFlagContainer}>
                  <img src={GermanFlag} alt='Germany Country Telephone Code' />
                </div>
                <span className={classes.countryCode}>+49</span>
                <div className={classes.smallLine}></div>
                <input
                  style={{ top : telephone ? "27.49px" : "" }}
                  className='slimRegularLarge'
                  type="number"
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <label className={`${classes.phoneLabel} ${isFocused ? classes.phoneFocused : ''}`}>
                  Telefonnummer eingeben
                </label>
              </div>
            ) : (
              <>
                <div className={classes.inputContainer} style={{ marginBottom: error ? '14.5px' : '30.01px'}}>
                  <label className={`${classes.label} ${isFocused || email ? classes.focused : ''}`}>
                    E-Mail eingeben
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>
                {emailSubmitted && (
                  <div className={classes.inputContainer} style={{ marginBottom: error ? '14.5px' : '30.01px', position: 'relative' }}>
                    <label className={`${classes.label} ${isFocused || password ? classes.focused : ''}`}>
                      Passwort eingeben
                    </label>
                    <input
                      className={classes.passwordInput}
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    <button
                      type="button"
                      className={`${classes.showPasswordButton} regularXs`}
                      onClick={() => setShowPassword(prev => !prev)}
                    >
                      {showPassword ? "Verbergen" : "Anzeigen"}
                    </button>
                  </div>
                )}
              </>
            )}
            {error && (
              <div className={classes.error}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
                </svg>
                {error?.includes('Nutzer existiert nicht') ? (
                  <span>
                    Nutzer existiert nicht. 
                    <button 
                      type="button" 
                      onClick={onSwitchToRegister}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: 'var(--stu-rot)', 
                        cursor: 'pointer',
                        padding: 0,
                        marginLeft: '4px'
                      }}
                    >
                      Möchtest du dich registrieren?
                    </button>
                  </span>
                ) : (
                  <span>{error}</span>
                )}
              </div>
            )}
            <button 
              style={{ marginBottom: emailSubmitted ? '20px' : '30px'}} 
              className={`${classes.submitButton} mediumLarge`} 
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Wird überprüft...' : 'Weiter'}
            </button>
          </form>
        )}
        
        {/* Forgot Password Form */}
        {showForgotPassword && !showResetConfirmation && (
          <form className={classes.form} onSubmit={handleForgotPasswordSubmit} noValidate>
            <div className={classes.inputContainer} style={{ marginBottom: error ? '14.5px' : '30.01px'}}>
              <label className={`${classes.label} ${isFocused || forgotPasswordEmail ? classes.focused : ''}`}>
                E-Mail eingeben
              </label>
              <input
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>
            {error && (
              <div className={classes.error}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
                </svg>
                <span>{error}</span>
              </div>
            )}
            <button 
              className={`${classes.submitButton} mediumLarge`} 
              type="submit"
              disabled={isSubmitting}
            >
              Code senden
            </button>
          </form>
        )}
        
        {/* Reset Password Confirmation Form */}
        {showForgotPassword && showResetConfirmation && (
          <form className={classes.form} onSubmit={handleResetPasswordSubmit} noValidate>
            <div className={classes.inputContainer} style={{ marginBottom: error ? '14.5px' : '20px'}}>
              <label className={`${classes.label} ${isCodeFocused || forgotPasswordConfirmationCode ? classes.focused : ''}`}>
                Bestätigungscode eingeben
              </label>
              <input
                type="text"
                value={forgotPasswordConfirmationCode}
                onChange={(e) => setForgotPasswordConfirmationCode(e.target.value)}
                onFocus={handleCodeFocus}
                onBlur={handleCodeBlur}
                maxLength={6}
              />
            </div>
            <div className={classes.inputContainer} style={{ marginBottom: error ? '14.5px' : '30.01px', position: 'relative' }}>
              <label className={`${classes.label} ${isNewPasswordFocused || newPassword ? classes.focused : ''}`}>
                Neues Passwort eingeben
              </label>
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onFocus={handleNewPasswordFocus}
                onBlur={handleNewPasswordBlur}
              />
              <button
                type="button"
                className={`${classes.showPasswordButton} regularXs`}
                onClick={() => setShowNewPassword(prev => !prev)}
              >
                {showNewPassword ? "Verbergen" : "Anzeigen"}
              </button>
            </div>
            {error && (
              <div className={classes.error}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
                </svg>
                <span>{error}</span>
              </div>
            )}
            <button 
              className={`${classes.submitButton} mediumLarge`} 
              type="submit"
              disabled={isSubmitting}
            >
              Passwort zurücksetzen
            </button>
          </form>
        )}
        
        {/* Email Confirmation Form */}
        {showEmailConfirmation && (
          <form className={classes.form} onSubmit={handleEmailConfirmationSubmit} noValidate>
            <div className={classes.telephoneInfosContainer}>
              <span className='regularLarge'>
                Gib den 6-stelligen Code ein, den wir an {auth.emailToConfirm || email} gesendet haben.
              </span>
            </div>
            <div className={classes.spamContainer}>
              <span className='regularSmall'>📩 Auch im SPAM-Ordner nachschauen!</span>
            </div>
            <div className={classes.inputContainer} style={{ marginBottom: error ? '14.5px' : '30.01px'}}>
              <label className={`${classes.label} ${isEmailConfirmationCodeFocused || emailConfirmationCode ? classes.focused : ''}`}>
                Bestätigungscode eingeben
              </label>
              <input
                ref={emailConfirmationInputRef}
                type="text"
                value={emailConfirmationCode}
                onChange={(e) => setEmailConfirmationCode(e.target.value)}
                onFocus={handleEmailConfirmationCodeFocus}
                onBlur={handleEmailConfirmationCodeBlur}
                maxLength={6}
              />
            </div>
            {error && (
              <div className={classes.error}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M1.02734 1.40723L10.9725 11.3524" stroke="#CB1541" strokeWidth="1.5" strokeMiterlimit="10"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M4.9452 6.37988L0.5 1.93469L1.5548 0.879883L6 5.32508L10.4452 0.879883L11.5 1.93469L7.0548 6.37988L11.5 10.8251L10.4452 11.8799L6 7.43469L1.5548 11.8799L0.5 10.8251L4.9452 6.37988Z" fill="#CB1541"/>
                </svg>
                <span>{error}</span>
              </div>
            )}
            <button 
              className={`${classes.submitButton} mediumLarge`} 
              type="submit"
              disabled={isSubmitting}
              style={{ marginBottom: '20px' }}
            >
              {isSubmitting ? 'Wird bestätigt...' : 'E-Mail bestätigen'}
            </button>
            <div className={classes.keinSmsComponent}>
              <span>Keine E-Mail erhalten? </span>
              <button 
                type="button"
                onClick={handleResendEmailConfirmation}
                disabled={resendingCode}
                className={classes.underlineSpan}
              >
                {resendingCode ? 'Wird gesendet...' : 'Erneut senden'}
              </button>
            </div>
          </form>
        )}
        
        {telephoneSubmitted && <OtpChecker telephone={telephone} onOtpSubmit={handleOtpSubmit} />}
        {emailSubmitted && !showTelephoneInput && !telephoneSubmitted && !showForgotPassword && !showEmailConfirmation && (
          <div className={classes.forgotPasswordContainer}>
            <button 
              type="button" 
              onClick={handleForgotPasswordClick}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'inherit', 
                cursor: 'pointer',
                padding: 0
              }}
            >
              Passwort vergessen?
            </button>
          </div>
        )}
        {!telephoneSubmitted && !showForgotPassword && !showEmailConfirmation && (
          <>
            <div className={classes.oderContainer}>
              <div></div>
              <span>oder</span>
              <div></div>
            </div>
            <SocialForm onTelephoneLogin={handleTelephoneLogin} resetFirstBoxClicked={resetSocialForm} />
            <div className={classes.registerContainer}>
              <span>Noch kein Konto?</span>
              <button onClick={onSwitchToRegister}>Hier registrieren</button>
            </div>
          </>
        )}
      </div>
      </div>
    </Modal>
  );
};

export default SignIn;
